import {
	ApolloClient,
	ApolloLink,
	ApolloQueryResult,
	FetchPolicy,
	FetchResult,
	HttpLink,
	InMemoryCache,
} from '@apollo/client/core';
import { AUTH_TYPE, AuthOptions, createAuthLink } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';

export interface AppSyncClientConfig {
	url: string;
	region: string;
	apiKey: string;
}

export interface LegacyObservableQuery<TData = unknown> {
	result(): Promise<ApolloQueryResult<TData>>;
}

export interface AppSyncGraphqlClient {
	watchQuery<TData = unknown>(options: Record<string, unknown>): LegacyObservableQuery<TData>;
	mutate<TData = unknown>(options: Record<string, unknown>): Promise<FetchResult<TData>>;
	query<TData = unknown>(options: Record<string, unknown>): Promise<ApolloQueryResult<TData>>;
}

export function createAppSyncApolloClient(config: AppSyncClientConfig): AppSyncGraphqlClient {
	const auth: AuthOptions = {
		type: AUTH_TYPE.API_KEY,
		apiKey: config.apiKey,
	};

	const httpLink = new HttpLink({ uri: config.url });
	const link = ApolloLink.from([
		createAuthLink({ url: config.url, region: config.region, auth }),
		createSubscriptionHandshakeLink({ url: config.url, region: config.region, auth }, httpLink),
	]);

	const client = new ApolloClient({
		link,
		cache: new InMemoryCache(),
		devtools: { enabled: false },
	});

	const withNoCache = (options: Record<string, unknown>) => ({
		...options,
		fetchPolicy: (options['fetchPolicy'] as FetchPolicy | undefined) ?? 'no-cache',
	}) as any;

	const normalizeQueryResult = <TData>(result: any) => ({
		...result,
		loading: false,
		networkStatus: 7,
		stale: false,
	}) as ApolloQueryResult<TData>;

	return {
		watchQuery: <TData = unknown>(options: Record<string, unknown>): LegacyObservableQuery<TData> => ({
			result: async () => normalizeQueryResult<TData>(await client.query(withNoCache(options))),
		}),
		mutate: <TData = unknown>(options: Record<string, unknown>) =>
			client.mutate(withNoCache(options)) as Promise<FetchResult<TData>>,
		query: async <TData = unknown>(options: Record<string, unknown>) =>
			normalizeQueryResult<TData>(await client.query(withNoCache(options))),
	};
}
